import { useEffect, useState } from 'react';
import useUserStore from '@/stores/UserStore';
import { Loader2Icon } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';

const LinkedInCallback = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing'); // 'processing', 'success', 'error'
  const [message, setMessage] = useState('Processing LinkedIn connection...');

  const { updateCustomerLinkedin } = useUserStore(
    useShallow((state) => ({
      updateCustomerLinkedin: state.updateCustomerLinkedin,
    }))
  );

  useEffect(() => {
    const handleLinkedInCallback = async () => {
      try {
        const params = new URLSearchParams(location.search);
        const accountId = params.get('account_id');
        const linkedinStatus = params.get('linkedin');
        const error = params.get('error');
        const errorDescription = params.get('error_description');

        // Access control: Check if we have valid OAuth parameters
        if (!accountId && !error) {
          toast.error(
            'Invalid callback parameters. This page should only be accessed through LinkedIn OAuth.'
          );
          navigate('/settings/linkedin', { replace: true });
          return;
        }

        // We're always in an iframe within the popup, so always send to parent
        const sendMessageToParent = (message) => {
          try {
            window.parent.postMessage(message, window.location.origin);
          } catch (error) {
            console.error(
              'LinkedInCallback: Failed to send message to parent:',
              error
            );
          }
        };

        // Handle OAuth errors
        if (error) {
          setStatus('error');
          setMessage(errorDescription || 'LinkedIn connection failed');

          // Notify parent window of failure
          sendMessageToParent({
            type: 'LINKEDIN_CONNECTED',
            success: false,
            message: errorDescription || 'LinkedIn connection failed',
            accountId: null, // No account ID for errors
          });

          // Don't close window from iframe - let the popup handle it
          return;
        }

        // Handle successful connection
        if (accountId) {
          setMessage('Processing LinkedIn connection...');

          setStatus('success');
          setMessage('LinkedIn connected successfully!');

          // Notify parent window of success (let main app handle webhook registration)
          const message = {
            type: 'LINKEDIN_CONNECTED',
            success: true,
            message: 'LinkedIn connected successfully!',
            linkedinStatus: linkedinStatus, // Pass status so main app knows if webhook is needed
            accountId: accountId, // Pass account ID for additional processing
          };
          sendMessageToParent(message);

          // Don't close window from iframe - let the popup handle it
        }
      } catch (error) {
        console.error('LinkedIn callback error:', error);
        setStatus('error');
        setMessage('An unexpected error occurred');

        // Notify parent window of failure
        try {
          window.parent.postMessage(
            {
              type: 'LINKEDIN_CONNECTED',
              success: false,
              message: 'An unexpected error occurred',
              accountId: null, // No account ID for errors
            },
            window.location.origin
          );
        } catch (error) {
          console.error(
            'LinkedInCallback: Failed to send error message to parent:',
            error
          );
        }
      }
    };

    handleLinkedInCallback();
  }, [location.search, updateCustomerLinkedin, navigate]);

  const getStatusColor = () => {
    switch (status) {
      case 'success':
        return 'text-green-600';
      case 'error':
        return 'text-red-600';
      default:
        return 'text-blue-600';
    }
  };

  const getStatusIcon = () => {
    if (status === 'processing') {
      return <Loader2Icon className='h-8 w-8 animate-spin text-blue-600' />;
    }
    return null;
  };

  return (
    <div className='flex min-h-screen items-center justify-center bg-background p-4'>
      <div className='max-w-md space-y-4 text-center'>
        <div className='flex justify-center'>{getStatusIcon()}</div>
        <h2 className='text-xl font-semibold'>LinkedIn Connection</h2>
        <p className={`text-lg ${getStatusColor()}`}>{message}</p>
        {status === 'success' ||
          (status === 'error' && (
            <p className='text-sm text-muted-foreground'>
              This window will close automatically...
            </p>
          ))}
      </div>
    </div>
  );
};

export default LinkedInCallback;
